import { supabase } from './supabase';
import type { LabResult } from './localData';

type LabResultRow = {
  id: string;
  household_id: string;
  owner: string;
  medical_profile_id: string | null;
  analyte: string;
  original_name: string | null;
  russian_name: string | null;
  international_name: string | null;
  canonical_code: string | null;
  aliases: string[] | null;
  country: string;
  lab_name: string | null;
  value: number | null;
  value_text: string | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  ref_text: string | null;
  panel: string | null;
  taken_on: string;
  document_id: string | null;
  source_page: number | null;
  source_row: number | null;
  source_section: string | null;
  source_document: string | null;
  notes: string | null;
};

function requireClient() {
  if (!supabase) throw new Error('Supabase не настроен');
  return supabase;
}

function rowToLabResult(row: LabResultRow): LabResult {
  return {
    id: row.id,
    owner: row.owner,
    medicalProfileId: row.medical_profile_id || undefined,
    analyte: row.original_name || row.analyte,
    originalName: row.original_name || row.analyte,
    russianName: row.russian_name || undefined,
    internationalName: row.international_name || undefined,
    canonicalCode: row.canonical_code || undefined,
    aliases: row.aliases || undefined,
    country: row.country === 'russia' || row.country === 'israel' ? row.country : 'other',
    labName: row.lab_name || undefined,
    value: row.value ?? undefined,
    valueText: row.value_text || undefined,
    unit: row.unit || undefined,
    refLow: row.ref_low ?? undefined,
    refHigh: row.ref_high ?? undefined,
    refText: row.ref_text || undefined,
    panel: row.panel || undefined,
    takenOn: row.taken_on,
    documentId: row.document_id || undefined,
    sourcePage: row.source_page ?? undefined,
    sourceRow: row.source_row ?? undefined,
    sourceSection: row.source_section || undefined,
    sourceDocument: row.source_document || undefined,
    notes: row.notes || undefined,
  };
}

export async function loadCloudLabResults(householdId: string): Promise<LabResult[]> {
  const client = requireClient();
  const { data, error } = await client
    .from('lab_results')
    .select('id, household_id, owner, medical_profile_id, analyte, original_name, russian_name, international_name, canonical_code, aliases, country, lab_name, value, value_text, unit, ref_low, ref_high, ref_text, panel, taken_on, document_id, source_page, source_row, source_section, source_document, notes')
    .eq('household_id', householdId)
    .is('deleted_at', null)
    .order('taken_on', { ascending: true });
  if (error) throw error;
  return ((data || []) as LabResultRow[]).map(rowToLabResult);
}

export async function createCloudLabResult(householdId: string, userId: string, result: LabResult): Promise<LabResult> {
  const client = requireClient();
  const { data: row, error } = await client.from('lab_results').insert({
    household_id: householdId,
    owner: result.owner,
    medical_profile_id: result.medicalProfileId || null,
    analyte: result.originalName || result.analyte,
    original_name: result.originalName || result.analyte,
    russian_name: result.russianName || null,
    international_name: result.internationalName || null,
    canonical_code: result.canonicalCode || null,
    aliases: result.aliases || [],
    country: result.country,
    lab_name: result.labName || null,
    value: result.value === undefined ? null : result.value,
    value_text: result.valueText || null,
    unit: result.unit || null,
    ref_low: result.refLow === undefined ? null : result.refLow,
    ref_high: result.refHigh === undefined ? null : result.refHigh,
    ref_text: result.refText || null,
    panel: result.panel || null,
    taken_on: result.takenOn,
    document_id: result.documentId || null,
    source_page: result.sourcePage ?? null,
    source_row: result.sourceRow ?? null,
    source_section: result.sourceSection || null,
    // Compatibility only: new cloud uploads keep the file in Storage and set
    // document_id instead of copying a base64 payload into lab_results.
    source_document: result.documentId ? null : result.sourceDocument || null,
    notes: result.notes || null,
    created_by: userId,
  }).select('id').single();
  if (error) throw error;
  return { ...result, id: row.id };
}

// The source-locator unique index protects against two devices confirming the
// same printed row at once. It must never compare the medical label or value.
export function isCloudLabResultDuplicateError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const value = error as { code?: unknown; message?: unknown; details?: unknown };
  if (value.code !== '23505') return false;
  return [value.message, value.details].some(part => typeof part === 'string' && /lab_results_document_source_locator_unique/.test(part));
}

// Identity fields (owner/analyte/country) are intentionally left untouched —
// those define which trend series a reading belongs to; changing them here
// would silently move a reading into a different series. documentId and
// sourceDocument are also left alone so an edited reading keeps its link back
// to the original photo/PDF (docs/AI_RULES.md "always keep the source").
export async function updateCloudLabResult(result: LabResult): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('lab_results').update({
    lab_name: result.labName || null,
    value: result.value === undefined ? null : result.value,
    value_text: result.valueText || null,
    unit: result.unit || null,
    ref_low: result.refLow === undefined ? null : result.refLow,
    ref_high: result.refHigh === undefined ? null : result.refHigh,
    ref_text: result.refText || null,
    panel: result.panel || null,
    taken_on: result.takenOn,
    notes: result.notes || null,
  }).eq('id', result.id);
  if (error) throw error;
}

export async function softDeleteCloudLabResult(id: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('lab_results').update({ deleted_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

export async function softDeleteCloudLabResultsForOwner(owner: LabResult['owner']): Promise<number> {
  const client = requireClient();
  const { data, error } = await client
    .from('lab_results')
    .update({ deleted_at: new Date().toISOString() })
    .eq('owner', owner)
    .is('deleted_at', null)
    .select('id');
  if (error) throw error;
  return data?.length || 0;
}

export async function softDeleteCloudLabResultsForDocument(documentId: string): Promise<number> {
  const client = requireClient();
  const { data, error } = await client
    .from('lab_results')
    .update({ deleted_at: new Date().toISOString() })
    .eq('document_id', documentId)
    .is('deleted_at', null)
    .select('id');
  if (error) throw error;
  return data?.length || 0;
}

// Bulk-correct every reading extracted from one document at once (e.g. the
// whole batch got the wrong exam date). Deliberately limited to takenOn and
// labName only — unlike updateCloudLabResult's per-row edit, this touches
// every reading of the document in one call, and owner/country/analyte are
// series identity (docs/AI_RULES.md, see the comment on updateCloudLabResult
// above): changing them here would silently move every one of those
// readings into a different trend series at once.
export async function bulkUpdateLabResultMetadataForDocument(documentId: string, patch: { takenOn?: string; labName?: string }): Promise<number> {
  const client = requireClient();
  const update: Record<string, unknown> = {};
  if (patch.takenOn !== undefined) update.taken_on = patch.takenOn;
  if (patch.labName !== undefined) update.lab_name = patch.labName || null;
  if (!Object.keys(update).length) return 0;
  const { data, error } = await client
    .from('lab_results')
    .update(update)
    .eq('document_id', documentId)
    .is('deleted_at', null)
    .select('id');
  if (error) throw error;
  return data?.length || 0;
}

export type LabResultNamePatch = Pick<LabResult, 'originalName' | 'russianName' | 'internationalName' | 'canonicalCode' | 'aliases'> & { id: string };

export async function enrichCloudLabResultNames(patch: LabResultNamePatch): Promise<void> {
  const client = requireClient();
  const originalName = patch.originalName?.trim();
  if (!originalName) throw new Error('Оригинальное название не может быть пустым');
  const { error } = await client.from('lab_results').update({
    analyte: originalName,
    original_name: originalName,
    russian_name: patch.russianName || null,
    international_name: patch.internationalName || null,
    canonical_code: patch.canonicalCode || null,
    aliases: patch.aliases || [],
  }).eq('id', patch.id).is('deleted_at', null);
  if (error) throw error;
}
